import { useEffect, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    ClickAwayListener,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    Typography
} from '@mui/material';

// third-party
import { FormattedMessage } from 'react-intl';
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import useAuth from 'hooks/useAuth';

// assets
import {
    IconQuestionMark
} from '@tabler/icons';
import useConfig from 'hooks/useConfig';

// ==============================|| HELP MENU ||============================== //

const HelpMenu = () => {
    const theme = useTheme();
    const { borderRadius } = useConfig();
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const { user } = useAuth();
    const [open, setOpen] = useState(false);

    const createSupportTicketUrl = 'https://kneadtech.atlassian.net/servicedesk/customer/portal/1/group/-1';
    const generalKnowledgeBaseUrl = 'https://kneadtech.atlassian.net/servicedesk/customer/portals'; // For Admins and Super Admins only
    const driverKnowledgeBaseUrl = 'https://kneadtech.atlassian.net/servicedesk/customer/topic/274d92bc-3a6e-42c8-ab3a-4ef98234a0fc'; // For Delivery Drivers only
    const donorRecipientKnowledgeBaseUrl = 'https://kneadtech.atlassian.net/servicedesk/customer/topic/fa0783ac-3025-43e8-8334-dade336b4cff'; // For Food Donors and Recipients only
    const gettingStartedAdminUrl = 'https://kneadtech.atlassian.net/servicedesk/customer/portal/1/article/442040457';
    const gettingStartedSuperAdminUrl = 'https://kneadtech.atlassian.net/servicedesk/customer/portal/1/article/442269743';
    const gettingStartedDeliveryDriverUrl = 'https://kneadtech.atlassian.net/servicedesk/customer/portal/1/article/442106261';
    const gettingStartedFoodDonorUrl = 'https://kneadtech.atlassian.net/servicedesk/customer/portal/1/article/442335236';
    const gettingStartedRecipientUrl = 'https://kneadtech.atlassian.net/servicedesk/customer/portal/1/article/442269701';

    /**
     * anchorRef is used on different components and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef<any>(null);

    const openInNewTab = (url: string): void => {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
            newWindow.opener = null;
        }
    }
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };
    const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
        if ((anchorRef.current && anchorRef.current.contains(event.target))
            || ((event.target as HTMLElement).localName === 'body')) { // Keep menu open when accessing the "active role" dropdown form
            return;
        }
        setSelectedIndex(-1); //added this to remove ListButton highlight when popup closes


        setOpen(false);
    };
    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    mr: 3,
                    [theme.breakpoints.down('md')]: {
                        mr: 2
                    }
                }}
            >
                <Avatar
                    variant="rounded"
                    sx={{
                        ...theme.typography.commonAvatar,
                        ...theme.typography.mediumAvatar,
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        color: theme.palette.primary.dark,
                        transition: 'all .2s ease-in-out',
                        '&[aria-controls="menu-list-grow"],&:hover': {
                            borderColor: theme.palette.primary.main,
                            background: theme.palette.primary.main,
                            color: theme.palette.primary.light
                        }
                    }}
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    color="inherit"
                >
                    <IconQuestionMark stroke={1.5} size="1.3rem" />
                </Avatar>
            </Box>

            <Popper
                placement="bottom"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 14]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                            <Box sx={{ p: 2, pt: 0 }}>
                                                <List
                                                    component="nav"
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 350,
                                                        minWidth: 300,
                                                        backgroundColor: theme.palette.background.paper,
                                                        borderRadius: '10px',
                                                        [theme.breakpoints.down('md')]: {
                                                            minWidth: '100%'
                                                        },
                                                        '& .MuiListItemButton-root': {
                                                            mt: 0.5
                                                        }
                                                    }}
                                                >
                                                    { ((user.role == "Super Admin") || (user.role == "Admin")) &&
                                                        <>
                                                            <ListItemButton
                                                            sx={{ borderRadius: `${borderRadius}px` }}
                                                            selected={selectedIndex === 1}
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                openInNewTab(createSupportTicketUrl)
                                                            }
                                                            >
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="body2">
                                                                        <FormattedMessage id="Open a Support Ticket" />
                                                                    </Typography>
                                                                }
                                                            />
                                                            </ListItemButton>
                                                        </>
                                                    }
                                                    { (user.role == "Super Admin") &&
                                                        <>
                                                            <ListItemButton
                                                            sx={{ borderRadius: `${borderRadius}px` }}
                                                            selected={selectedIndex === 2}
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                openInNewTab(gettingStartedSuperAdminUrl)
                                                            }
                                                            >
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="body2">
                                                                        <FormattedMessage id="Getting Started Guide for Super Admins" />
                                                                    </Typography>
                                                                }
                                                            />
                                                            </ListItemButton>
                                                            <ListItemButton
                                                            sx={{ borderRadius: `${borderRadius}px` }}
                                                            selected={selectedIndex === 3}
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                openInNewTab(generalKnowledgeBaseUrl)
                                                            }
                                                            >
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="body2">
                                                                        <FormattedMessage id="Knowledge Base" />
                                                                    </Typography>
                                                                }
                                                            />
                                                            </ListItemButton>
                                                        </>
                                                    }
                                                    { (user.role == "Admin") &&
                                                        <>
                                                            <ListItemButton
                                                            sx={{ borderRadius: `${borderRadius}px` }}
                                                            selected={selectedIndex === 2}
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                openInNewTab(gettingStartedAdminUrl)
                                                            }
                                                            >
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="body2">
                                                                        <FormattedMessage id="Getting Started Guide for Admins" />
                                                                    </Typography>
                                                                }
                                                            />
                                                            </ListItemButton>
                                                            <ListItemButton
                                                            sx={{ borderRadius: `${borderRadius}px` }}
                                                            selected={selectedIndex === 3}
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                openInNewTab(generalKnowledgeBaseUrl)
                                                            }
                                                            >
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="body2">
                                                                        <FormattedMessage id="Knowledge Base" />
                                                                    </Typography>
                                                                }
                                                            />
                                                            </ListItemButton>
                                                        </>
                                                    }
                                                    {(user.role == "Delivery Driver" || user.role == "Food Donor" || user.role == "Recipient") &&
                                                        <>
                                                            <ListItemButton
                                                                sx={{ borderRadius: `${borderRadius}px` }}
                                                                selected={selectedIndex === 1}
                                                                onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                    openInNewTab(user.role === 'Delivery Driver' ? gettingStartedDeliveryDriverUrl :
                                                                      (user.role === 'Food Donor' ? gettingStartedFoodDonorUrl : gettingStartedRecipientUrl))
                                                                }
                                                            >
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography variant="body2">
                                                                            <FormattedMessage id="Getting Started Guide" />
                                                                        </Typography>
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                            <ListItemButton
                                                                sx={{ borderRadius: `${borderRadius}px` }}
                                                                selected={selectedIndex === 2}
                                                                onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                                    openInNewTab(user.role === 'Delivery Driver' ? driverKnowledgeBaseUrl : donorRecipientKnowledgeBaseUrl)
                                                                }
                                                            >
                                                                <ListItemText
                                                                    primary={
                                                                        <Typography variant="body2">
                                                                            <FormattedMessage id="Knowledge Base" />
                                                                        </Typography>
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        </>
                                                    }
                                                </List>
                                            </Box>
                                        </PerfectScrollbar>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default HelpMenu;
